import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { List, X, QrCode } from "@phosphor-icons/react";
import { useLang } from "../context/LangContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-slate-200" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
            <div className="flex h-8 w-8 items-center justify-center bg-[#0A0F1A] rounded-[4px] group-hover:bg-emerald-500 transition-colors duration-300">
              <QrCode size={18} weight="bold" className="text-white" />
            </div>
            <span className="font-display font-bold text-[17px] tracking-tight text-[#0F172A]">Certifico<span className="text-emerald-500">QR</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            <button onClick={() => scrollTo("features")} className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-features">{t.nav.features}</button>
            <button onClick={() => scrollTo("how")} className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-how">{t.nav.how}</button>
            <button onClick={() => scrollTo("sectors")} className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-sectors">{t.nav.sectors}</button>
            <button onClick={() => scrollTo("libro")} className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-book">{t.nav.book}</button>
            <button onClick={() => scrollTo("pricing")} className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-pricing">{t.nav.pricing}</button>
            <button onClick={() => scrollTo("contatti")} className="text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-contact">{t.nav.contact}</button>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              className="hidden sm:flex items-center font-mono-tech text-xs font-semibold border border-slate-300 rounded-[4px] overflow-hidden"
              data-testid="lang-toggle"
            >
              <span className={`px-2 py-1 transition-colors ${lang === "it" ? "bg-[#0F172A] text-white" : "text-slate-500"}`}>IT</span>
              <span className={`px-2 py-1 transition-colors ${lang === "en" ? "bg-[#0F172A] text-white" : "text-slate-500"}`}>EN</span>
            </button>
            <Link to="/login" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-[#0F172A] transition-colors" data-testid="navbar-login">{t.nav.login}</Link>
            <Link
              to="/register"
              className="hidden sm:inline-flex items-center rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors duration-300"
              data-testid="navbar-cta"
            >
              {t.nav.cta}
            </Link>
            <button className="md:hidden text-[#0F172A]" onClick={() => setOpen(!open)} data-testid="navbar-mobile-toggle">
              {open ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 px-6 py-4 space-y-3">
          <button onClick={() => { scrollTo("features"); setOpen(false); }} className="block text-sm font-medium text-slate-700">{t.nav.features}</button>
          <button onClick={() => { scrollTo("how"); setOpen(false); }} className="block text-sm font-medium text-slate-700">{t.nav.how}</button>
          <button onClick={() => { scrollTo("sectors"); setOpen(false); }} className="block text-sm font-medium text-slate-700" data-testid="navbar-mobile-sectors">{t.nav.sectors}</button>
          <button onClick={() => { scrollTo("libro"); setOpen(false); }} className="block text-sm font-medium text-slate-700" data-testid="navbar-mobile-book">{t.nav.book}</button>
          <button onClick={() => { scrollTo("pricing"); setOpen(false); }} className="block text-sm font-medium text-slate-700" data-testid="navbar-mobile-pricing">{t.nav.pricing}</button>
          <button onClick={() => { scrollTo("contatti"); setOpen(false); }} className="block text-sm font-medium text-slate-700" data-testid="navbar-mobile-contact">{t.nav.contact}</button>
          <Link to="/login" className="block text-sm font-medium text-slate-700">{t.nav.login}</Link>
          <div className="flex items-center gap-3 pt-2">
            <button onClick={toggle} className="font-mono-tech text-xs border border-slate-300 rounded-[4px] px-2 py-1">{lang.toUpperCase()}</button>
            <Link to="/register" onClick={() => setOpen(false)} className="inline-flex rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-[#04170F]">{t.nav.cta}</Link>
          </div>
        </div>
      )}
    </motion.header>
  );
};
